import React, { useRef, useCallback, ReactNode } from 'react';
import Loading from './Loading';
import NoData from './Nodata';
import NoDataTextComponent from './NoDataTextComponent';
import CardLoaderSection from '../Pages/Dashboard/components/SkeletonLoader/CardLoaderSection';
import DataCardLoader from '../Pages/Dashboard/components/SkeletonLoader/DataCardLoader';
import ImgCardLoader from '../Pages/Dashboard/components/SkeletonLoader/ImgCardLoader';
import CategoryLoader from '../Pages/Store/Components/SkeletonLoader/CategoryLoader';
import GroupLoader from '../Pages/Store/Components/SkeletonLoader/GroupLoader';
import StoreCardLoader from '../Pages/Dashboard/components/SkeletonLoader/StoreCardLoader';

interface InfiniteScrollProps {
  children: JSX.Element;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  loadingComponent?: ReactNode;
  className?: string;
  noData?: boolean;
  noDataText?: string;
  isShowNoData?: boolean;
  isOrder?: boolean;
  handleClick?: any;
  buttonText?: any;
  title?: any;
  imgCard?: boolean;
  isContactLoading?: boolean;
  isCategory?: boolean;
  isVendor?: boolean;
  isStoreCard?: boolean;
  bannerNoData?: boolean;
  isPayment?: boolean;
  isDefault?: boolean;
  isOrderSetting?: boolean;
  isNotes?: boolean;
  isLocation?: boolean;
  queryLoader?: ReactNode;
  isStoreSearch?: boolean;
}

/**
 * Handles the infinite scrolling behavior with optional loading states and no data display.
 * @example
 * infiniteScrollComponent(props)
 * // Renders an infinite scroll component with children elements and loading indicators.
 * @param {Object} props - The properties object.
 * @param {ReactNode} props.children - Elements to be displayed inside the scroll component.
 * @param {boolean} props.hasMore - A flag indicating if more data is available to scroll.
 * @param {function} props.onLoadMore - Callback to load more data when user scrolls to the end.
 * @param {boolean} [props.isLoading=false] - Indicates if data is currently being loaded.
 * @param {ReactNode} [props.loadingComponent] - Custom component to display while loading.
 * @param {boolean} props.noData - Flag to check if there's no data to display.
 * @param {string} props.noDataText - Text to display when there's no data.
 * @param {boolean} props.isShowNoData - Indicates whether to show a no data message.
 * @param {boolean} props.isOrder - Flag for order-related loading component.
 * @param {function} props.handleClick - Click handler for the no data button.
 * @param {string} props.buttonText - Text for the no data button.
 * @param {string} props.title - Title text for no data section.
 * @param {boolean} props.imgCard - Flag for image card loading component.
 * @param {boolean} props.isContactLoading - Flag indicating loading state for contacts.
 * @param {boolean} props.isCategory - Flag for category-related loading component.
 * @param {boolean} props.isVendor - Flag for vendor-related loading component.
 * @param {boolean} props.isStoreCard - Flag for store card loading component.
 * @param {boolean} props.bannerNoData - Flag to display banner-style no data indicator.
 * @returns {JSX.Element} The rendered infinite scroll component with conditional children and loading states.
 * @description
 *   - Utilizes Intersection Observer API to trigger loading of more data.
 *   - Disconnects observer to prevent redundant data fetching.
 *   - Renders varying loading components based on flags provided.
 *   - Supports custom rendering for "no data" scenarios.
 */
const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  children,
  hasMore,
  onLoadMore,
  isLoading = false,
  loadingComponent,
  noData,
  noDataText,
  isShowNoData,
  isOrder,
  handleClick,
  buttonText,
  title,
  imgCard,
  isContactLoading,
  isCategory,
  isVendor,
  isStoreCard,
  bannerNoData,
  isPayment,
  isDefault,
  isOrderSetting,
  isNotes,
  isLocation,
  queryLoader,
  isStoreSearch,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreFiredRef = useRef(false);

  React.useEffect(() => {
    if (!isLoading) {
      loadMoreFiredRef.current = false;
    }
  }, [isLoading]);

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || !hasMore || noData) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !loadMoreFiredRef.current) {
            loadMoreFiredRef.current = true;
            onLoadMore && onLoadMore();
          }
        },
        { threshold: 0.1 },
      );

      if (node) observerRef.current.observe(node);
    },
    [hasMore, isLoading, onLoadMore, noData],
  );

  return (
    <>
      {noData ? (
        isShowNoData ? (
          <NoData
            // img={false}
            heading={noDataText}
            handleClick={handleClick}
            buttonText={buttonText}
            title={title}
            isStoreSearch={isStoreSearch}
          />
        ) : bannerNoData ? (
          <NoData
            img={false}
            title={noDataText}
            className="detail-no-record pt-24 light"
          />
        ) : (
          <NoDataTextComponent />
        )
      ) : null}
      <div>
        {/* className={`infiniteScroll ${className}`} */}
        {children ? children : null}
        <div
          ref={lastElementRef}
          style={{ height: isLoading || !hasMore ? '0px' : '20px' }}
        />
        {isLoading &&
          (loadingComponent ? (
            loadingComponent
          ) : queryLoader ? (
            queryLoader
          ) : isOrder ? (
            <DataCardLoader className="elevate" />
          ) : imgCard ? (
            <ImgCardLoader />
          ) : isCategory ? (
            <CategoryLoader />
          ) : isVendor ? (
            <GroupLoader className="elevate" />
          ) : isContactLoading ? (
            <p className="text-white d-flex align-center">
              {/* <CircularProgress size={20} /> */}
              <span className="ml-12">Loading...</span>
            </p>
          ) : isStoreCard ? (
            <StoreCardLoader />
          ) : isPayment ? (
            <div className="payment-cards">
              <div className="debit-card-loader">
                <p className="lg mb-24"></p>
                <p className="sm"></p>
                <p className="xs"></p>
              </div>
              <div className="debit-card-loader">
                <p className="lg mb-24"></p>
                <p className="sm"></p>
                <p className="xs"></p>
              </div>
              <div className="debit-card-loader">
                <p className="lg mb-24"></p>
                <p className="sm"></p>
                <p className="xs"></p>
              </div>
            </div>
          ) : isDefault || isOrderSetting ? (
            <div style={{ ...(isOrderSetting && { marginTop: '20%' }) }}>
              <Loading type="div" />
            </div>
          ) : isNotes ? (
            <Loading type="div" height="auto" />
          ) : isLocation ? (
            <Loading type="div" height="auto" />
          ) : (
            <CardLoaderSection />
          ))}
      </div>
    </>
  );
};

export default InfiniteScroll;
